// Renomeia uma entrada do cofre.
//
// Só o rótulo muda — nenhuma configuração vinculada é afetada. Existe porque o
// nome é o que identifica a entrada no combo da configuração, e um rótulo
// genérico deixa de servir assim que o merchant tem mais de um certificado.
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { useRenameMerchantMtlsCertificateMutation } from "@/services/admin/merchant-mtls-certificates"

const panelPrefix = "pages.adminMerchants"

const buildSchema = (required: string) =>
  z.object({
    name: z.string().trim().min(1, required),
  })

type RenameMtlsCertificateValues = z.infer<
  ReturnType<typeof buildSchema>
>

type RenameMtlsCertificateDialogProps = {
  certificateId: string
  certificateName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const RenameMtlsCertificateDialog = ({
  certificateId,
  certificateName,
  open,
  onOpenChange,
}: RenameMtlsCertificateDialogProps) => {
  const { t } = useTranslation()
  const renameMutation =
    useRenameMerchantMtlsCertificateMutation(certificateId)

  const schema = useMemo(
    () =>
      buildSchema(
        t("pages.adminProcessorConfig.form.required")
      ),
    [t]
  )

  const form = useForm<RenameMtlsCertificateValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: certificateName },
  })

  // O nome chega depois do fetch do detalhe: sem isto o campo abriria vazio.
  useEffect(() => {
    if (open) form.reset({ name: certificateName })
  }, [open, certificateName, form])

  const handleSubmit = form.handleSubmit((values) => {
    renameMutation.mutate(values.name, {
      onSuccess: () => onOpenChange(false),
    })
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(
              `${panelPrefix}.mtlsCertificates.renameTitle`
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              `${panelPrefix}.mtlsCertificates.renameDescription`
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(
                      `${panelPrefix}.mtlsCertificates.name`
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} autoFocus />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={renameMutation.isPending}
              >
                {t(
                  `${panelPrefix}.mtlsCertificates.renameSubmit`
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
